import React, { useMemo, useState } from 'react';
import { ImageIcon, SearchInput } from '@saleshandy/design-system';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import classNames from 'classnames';

import { useSelector } from 'react-redux';
import Modal from '../../../ui/modal';
import Checkbox from '../../atoms/checkbox';
import DNDField from './components/dnd-field';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';
import { truncate } from '../../../../utils';
import { RootState } from '../../../../../store/root-reducer';
import { Images } from '../../../../app-constants';

type Field = {
  id: string | number;
  label: string;
  isDefault?: boolean;
  isNonRemovable?: boolean;
};

type IProps = {
  show: boolean;
  onCancel: VoidFunction;
  onSave: (fields: Field[], newlyAddedColumns: Field[]) => void;

  selectedFields: Field[];
  columnGroups: {
    id: string;
    title: string;
    fields: Field[];
  }[];
  priorityArray?: string[];
  onResetColumns: VoidFunction;
  showSelectAll?: boolean;
};

const EditTableColumnsModal: React.FC<IProps> = ({
  show,
  onCancel,
  onSave,

  selectedFields,
  columnGroups,
  priorityArray,
  onResetColumns,
  showSelectAll = false,
}) => {
  const [search, setSearch] = useState('');
  const [columns, setColumns] = useState(columnGroups);
  const [removableColumns, setRemovableColumns] = useState<Field[]>([]);
  const [nonRemovableColumns, setNonRemovableColumns] = useState<Field[]>([]);
  const [newlyAddedColumns, setNewlyAddedColumns] = useState<Field[]>([]);

  const fields = useSelector((state: RootState) => state?.prospect?.fields);

  useMemo(() => {
    setRemovableColumns(selectedFields.filter((f) => !f?.isNonRemovable));
    setNonRemovableColumns(selectedFields.filter((f) => f?.isNonRemovable));
  }, [selectedFields]);

  useMemo(() => {
    setColumns(columnGroups);
  }, [columnGroups]);

  const getIsFieldSelected = (field: Field): boolean =>
    !!removableColumns.find((f) => f.id === field.id);

  const sortByPriority = (cols: Field[]): Field[] => {
    if (!priorityArray?.length) return cols;

    return [...cols].sort((a, b) => {
      const indexA = priorityArray.indexOf(a.id.toString());
      const indexB = priorityArray.indexOf(b.id.toString());

      return (
        (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
        (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
      );
    });
  };

  const getDefaultRemovableFields = (): Field[] => {
    const allFields = columnGroups.flatMap((group) => group.fields);
    const defaultFields = allFields.filter(
      (field) => !field?.isNonRemovable && field?.isDefault,
    );
    return sortByPriority(defaultFields);
  };

  const syncNewlyAddedColumns = (nextRemovableColumns: Field[]) => {
    const nextIds = new Set(nextRemovableColumns.map((f) => f.id));
    const nextNewlyAdded = nextRemovableColumns.filter(
      (field) =>
        nextIds.has(field.id) && !selectedFields.find((f) => f.id === field.id),
    );
    setNewlyAddedColumns(nextNewlyAdded);
  };

  const handleOnToggle = (field: Field) => {
    if (field?.isNonRemovable) return;

    const isFieldSelected = getIsFieldSelected(field);

    // Case: Uncheck the checkbox
    if (isFieldSelected) {
      if (!field?.isNonRemovable) {
        setRemovableColumns(removableColumns.filter((f) => f.id !== field.id));
        setNewlyAddedColumns(
          newlyAddedColumns.filter((f) => f.id !== field.id),
        );
      }
      return;
    }

    if (priorityArray?.length > 0) {
      // Add the field and sort the selected items based on the priorityArray
      const updatedColumns = [...removableColumns, field];
      // Sort the selected items based on the priorityArray
      updatedColumns.sort((a, b) => {
        const indexA = priorityArray.indexOf(a.id.toString());
        const indexB = priorityArray.indexOf(b.id.toString());

        // If a field is not in the priorityArray, assign it a large index value to put it at the end
        return (
          (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
          (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
        );
      });

      setRemovableColumns(updatedColumns);
    } else {
      setRemovableColumns([...removableColumns, field]);
    }

    const isNewField = !selectedFields.find((f) => f.id === field.id);
    if (isNewField) {
      setNewlyAddedColumns([...newlyAddedColumns, field]);
    }
  };

  const handleOnSave = () => {
    onSave([...nonRemovableColumns, ...removableColumns], newlyAddedColumns);
  };

  const getFieldIndex = (fieldId) =>
    removableColumns.findIndex((field) => field.id === fieldId);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) return;

    setRemovableColumns((cols) => {
      const from = getFieldIndex(active.id);
      const to = getFieldIndex(over.id);

      const newCols = arrayMove(cols, from, to);

      return newCols;
    });
  };

  const onSearchChange = (searchText: string) => {
    setSearch(searchText);

    if (!searchText) {
      setColumns(columnGroups);
    }

    const lowercasedSearchText = searchText.toLowerCase();

    setColumns(
      columnGroups
        .map((group) => ({
          ...group,
          fields: group.fields.filter((field) =>
            field.label.toLowerCase().includes(lowercasedSearchText),
          ),
        }))
        .filter((group) => group.fields.length > 0),
    );
  };

  const getFieldClassNames = (field: Field) =>
    classNames([
      'field',
      {
        checked: field?.isNonRemovable || getIsFieldSelected(field),
      },
    ]);

  const getAllAvailableFields = (): Field[] =>
    columns.flatMap((group) =>
      group.fields.filter((field) => !field?.isNonRemovable),
    );

  // Check if all available fields are selected
  const areAllFieldsSelected = (): boolean => {
    const availableFields = getAllAvailableFields();
    if (availableFields.length === 0) return false;
    return availableFields.every((field) => getIsFieldSelected(field));
  };

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    const availableFields = columns.flatMap((group) =>
      group.fields.filter((field) => !field?.isNonRemovable),
    );
    const allSelected = areAllFieldsSelected();

    if (allSelected) {
      // Deselect all fields except defaults
      const defaultFields = getDefaultRemovableFields();
      setRemovableColumns(defaultFields);
      syncNewlyAddedColumns(defaultFields);
    } else {
      // Select all fields
      const fieldsToAdd = availableFields.filter(
        (field) => !getIsFieldSelected(field),
      );

      if (fieldsToAdd.length > 0) {
        let updatedColumns: Field[];

        if (priorityArray?.length > 0) {
          // Add fields and sort based on priorityArray
          updatedColumns = [...removableColumns, ...fieldsToAdd];
          updatedColumns.sort((a, b) => {
            const indexA = priorityArray.indexOf(a.id.toString());
            const indexB = priorityArray.indexOf(b.id.toString());
            return (
              (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
              (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
            );
          });
        } else {
          updatedColumns = [...removableColumns, ...fieldsToAdd];
        }

        setRemovableColumns(updatedColumns);

        // Track newly added columns
        const newFields = fieldsToAdd.filter(
          (field) => !selectedFields.find((f) => f.id === field.id),
        );
        if (newFields.length > 0) {
          setNewlyAddedColumns([...newlyAddedColumns, ...newFields]);
        }
      }
    }
  };

  const handleResetColumns = () => {
    const defaultFields = getDefaultRemovableFields();
    setRemovableColumns(defaultFields);
    syncNewlyAddedColumns(defaultFields);
    onResetColumns();
  };

  return (
    <Modal
      show={show}
      title="Edit Table Column"
      className="edit-table-column"
      variant="border"
      showCloseButton
      onHide={onCancel}
      primaryBtnClickedHandler={handleOnSave}
      secondaryBtnClickedHandler={onCancel}
      footer={
        <div
          className="footer-reset-col-button"
          {...accessibleOnClick(handleResetColumns)}
        >
          <p>Reset Columns</p>
        </div>
      }
    >
      <div className="edit-table-container">
        <div className="col-left">
          <h3 className="col-title">Available Fields</h3>
          <SearchInput
            search={search}
            setSearch={onSearchChange}
            onSearch={() => null}
            onClearSearch={() => {
              onSearchChange('');
            }}
          />

          {showSelectAll && (
            <div
              typeof="button"
              className="select-all-field"
              {...accessibleOnClick(handleSelectAllToggle)}
            >
              <Checkbox
                id="select-all-fields"
                checked={areAllFieldsSelected()}
                onChange={handleSelectAllToggle}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="field-label-name">Select All Field</span>
            </div>
          )}

          {columns
            ?.filter((group) =>
              group?.fields?.some((field) => !field?.isNonRemovable),
            )
            .map((group) => (
              <div className="fields-section" key={group.id}>
                <h4 className="fields-title">{group.title}</h4>
                <div className="fields-container">
                  {group.fields.map(
                    (field) =>
                      !field?.isNonRemovable && (
                        <div
                          className={getFieldClassNames(field)}
                          key={field.id}
                        >
                          <label
                            htmlFor={field.id.toString()}
                            className="field-label"
                          >
                            <Checkbox
                              id={field.id.toString()}
                              checked={
                                field?.isNonRemovable ||
                                getIsFieldSelected(field)
                              }
                              onChange={() => handleOnToggle(field)}
                            />

                            <span className="field-label-name">
                              {field?.label}
                              {/* {truncate(field?.label || '', 25, false)} */}
                            </span>
                            {fields?.find(
                              (f) =>
                                f?.id?.toString() === field?.id?.toString(),
                            )?.isAiGenerated && (
                              <ImageIcon
                                src={Images.NoResultGradient}
                                alt="ai-icon"
                                width={16}
                                height={16}
                              />
                            )}
                          </label>
                        </div>
                      ),
                  )}
                </div>
              </div>
            ))}
        </div>

        <div className="col-right">
          <h3 className="col-title">Shown in Table</h3>

          <div className="dnd-fields-container">
            <div className="dnd-fields">
              {nonRemovableColumns?.map((field) => (
                <div className="dnd-field non-removable" key={field?.id}>
                  <span>{field?.label}</span>
                </div>
              ))}
            </div>

            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={onDragEnd}
            >
              <div className="dnd-fields">
                <SortableContext
                  items={removableColumns}
                  strategy={verticalListSortingStrategy}
                >
                  {removableColumns?.map((field) => (
                    <DNDField
                      key={field?.id}
                      field={field}
                      handleOnToggle={handleOnToggle}
                      fields={fields}
                    />
                  ))}
                </SortableContext>
              </div>
            </DndContext>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditTableColumnsModal;
